import React, { useEffect, useState, useContext } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, Download, Loader, GraduationCap } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../../App';
import Footer from '../Footer';
import ReturnToAppButton from '../ReturnToAppButton';

const API = process.env.REACT_APP_BACKEND_URL || '';

const LMSPaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [invoiceData, setInvoiceData] = useState(null);
  
  const courseName = searchParams.get('course');
  const orderId = searchParams.get('order_id');

  useEffect(() => {
    if (!user) {
      navigate('/');
      return;
    }
    if (orderId) {
      fetchInvoice();
    } else {
      setLoading(false);
    }
  }, [orderId, user]);

  const fetchInvoice = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/payments/invoice/${orderId}`,
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      
      if (response.data.success) {
        setInvoiceData(response.data);
      }
    } catch (error) {
      console.error('Failed to fetch invoice:', error);
      toast.error('Failed to load invoice');
    } finally {
      setLoading(false);
    }
  };

  const downloadInvoice = (format) => {
    if (!invoiceData) return;
    
    if (format === 'html') {
      const blob = new Blob([invoiceData.invoice_html], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice_${orderId}.html`;
      a.click();
      window.URL.revokeObjectURL(url);
    } else if (format === 'pdf') {
      // Open invoice in new window and trigger print dialog
      const printWindow = window.open('', '_blank');
      printWindow.document.write(invoiceData.invoice_html);
      printWindow.document.close();
      setTimeout(() => {
        printWindow.print();
      }, 250);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader className="h-12 w-12 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50">
      <div className="flex items-center justify-center p-4 py-12">
        <Card className="max-w-2xl w-full">
          <CardContent className="p-8 text-center">
            <div className="relative mb-6">
              <CheckCircle className="h-20 w-20 text-green-500 mx-auto" />
              <GraduationCap className="h-8 w-8 text-purple-600 absolute top-0 right-1/3 transform translate-x-1/2" />
            </div>
            
            <h1 className="text-3xl font-bold text-gray-900 mb-4">
              Enrollment Successful! 🎉
            </h1>
            
            <p className="text-xl text-gray-700 mb-6">
              You are now enrolled in <strong className="text-purple-600">{courseName}</strong>
            </p>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <p className="text-green-700">
                Your course is now available in "My Learning". Start learning today!
              </p>
            </div>
            
            {invoiceData && (
              <div className="bg-white border-2 border-purple-200 rounded-lg p-6 mb-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Download Your Invoice</h2>
                <div className="flex gap-4 justify-center">
                  <Button
                    onClick={() => downloadInvoice('html')}
                    className="bg-blue-700 hover:bg-blue-700 text-white"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download HTML
                  </Button>
                  <Button
                    onClick={() => downloadInvoice('pdf')}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Print PDF
                  </Button>
                </div>
              </div>
            )}
            
            <div className="space-y-3">
              <ReturnToAppButton />
              <Button
                onClick={() => navigate('/my-learning')}
                className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90 text-white font-semibold py-6 text-lg"
              >
                <GraduationCap className="h-5 w-5 mr-2" />
                Go to My Learning
              </Button>
              <Button
                onClick={() => navigate('/courses')}
                variant="outline"
                className="w-full"
              >
                Browse More Courses
              </Button>
              <Button
                onClick={() => navigate('/profile')}
                variant="ghost"
                className="w-full text-gray-600"
              >
                View Payment History
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
};

export default LMSPaymentSuccess;
